import React, { useEffect, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useGetIdentity, useNotify, useTranslate, Title } from "react-admin";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Person as PersonIcon } from "@mui/icons-material";
import { SectionHeader } from "@/components/common";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import {
  EMAIL_MAX_LENGTH,
  EMAIL_MIN_LENGTH,
  validateEmail,
} from "@/utils/validation";
import PasswordResetDialog from "./PasswordResetDialog";
import {
  getUserProfileById,
  updateUserProfile,
} from "../services/userProfileService";
import type { UserProfileFormValues } from "../types/userProfile";

const emptyForm: UserProfileFormValues = {
  name: "",
  surname: "",
  email: "",
};

export const UserProfilePage: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const { data: identity, isLoading: identityLoading } = useGetIdentity();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);

  const roleIdRef = useRef<string>("");
  const userIdRef = useRef<string>("");

  const {
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors, isDirty },
  } = useForm<UserProfileFormValues>({
    defaultValues: emptyForm,
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
    shouldFocusError: true,
  });

  useEffect(() => {
    if (identityLoading) return;
    const id = identity?.id != null ? String(identity.id) : "";
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const profile = await getUserProfileById(id);
        if (cancelled) return;
        userIdRef.current = profile.id ?? "";
        roleIdRef.current = profile.role?.name ?? profile.roleId ?? "";
        reset({
          name: profile.name ?? "",
          surname: profile.surname ?? "",
          email: profile.email ?? "",
        });
      } catch (error: unknown) {
        notify(
          extractErrorMessage(
            error,
            "user-profile.notifications.loadFailed",
            translate,
          ),
          { type: "error" },
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identity?.id, identityLoading, reset, notify, translate]);

  const onSubmit = async (values: UserProfileFormValues): Promise<void> => {
    if (!userIdRef.current) return;
    setSaving(true);
    try {
      await updateUserProfile({
        id: userIdRef.current,
        name: values.name,
        surname: values.surname,
        email: values.email,
        roleId: roleIdRef.current,
      });
      notify("user-profile.notifications.profileUpdated", { type: "success" });
      reset(values);
    } catch (error: unknown) {
      notify(
        extractErrorMessage(
          error,
          "user-profile.notifications.saveFailed",
          translate,
        ),
        { type: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  if (identityLoading || loading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 6,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Title title={translate("user-profile.pageTitle")} />
      <Box sx={{ width: "100%", p: { xs: 2, sm: 3 } }}>
        <Card sx={{ border: 1, borderColor: "divider", mb: 3 }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <SectionHeader
              title={translate("user-profile.sections.personalInfo")}
              icon={<PersonIcon />}
            />
            <Divider sx={{ mb: 3 }} />

            <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={3}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={translate("user-profile.fields.name")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      helperText={" "}
                    />
                  )}
                />
                <Controller
                  name="surname"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label={translate("user-profile.fields.surname")}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      helperText={" "}
                    />
                  )}
                />
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: translate("user-profile.validation.emailRequired"),
                    validate: (value) => {
                      const key = validateEmail(value ?? "");
                      return key == null
                        ? true
                        : translate(key, {
                            min: EMAIL_MIN_LENGTH,
                            max: EMAIL_MAX_LENGTH,
                          });
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onBlur={() => {
                        field.onBlur();
                        void trigger("email");
                      }}
                      label={translate("user-profile.fields.email")}
                      type="email"
                      required
                      fullWidth
                      error={Boolean(errors.email)}
                      helperText={errors.email?.message ?? " "}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ maxLength: EMAIL_MAX_LENGTH }}
                    />
                  )}
                />

                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                >
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    onClick={() => setPasswordDialogOpen(true)}
                  >
                    {translate("user-profile.actions.resetPassword")}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!isDirty || saving}
                  >
                    {saving ? (
                      <CircularProgress
                        size={20}
                        sx={{ color: "common.white" }}
                      />
                    ) : (
                      <Typography component="span" sx={{ fontWeight: 600 }}>
                        {translate("user-profile.actions.save")}
                      </Typography>
                    )}
                  </Button>
                </Box>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <PasswordResetDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
      />
    </>
  );
};

export default UserProfilePage;
